import {
  ReportExportRequest,
  ReportExportResponse,
  ReportEmailRequest,
  ReportEmailResponse,
} from "../models/ReportExport.model";
import { environment } from "../environments";

const API_BASE_URL = environment.apiUrl;

/**
 * Service לטיפול בקריאות API של ייצוא דוחות
 */
export class ReportExportService {
  /**
   * ייצוא דוח לפורמט מבוקש (PDF/Excel)
   */
  static async exportReport(
    reportData: Omit<
      ReportExportRequest,
      "exportFormat" | "token" | "companyKey"
    >,
    exportFormat: "pdf" | "excel" | "csv",
    token: string,
    companyKey: string,
  ): Promise<ReportExportResponse> {
    const request: ReportExportRequest = {
      ...reportData,
      exportFormat,
      token,
      companyKey,
    };

    try {
      const response = await fetch(`${API_BASE_URL}ReportsExport/Export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const error = await response.json();
        return error;
      }

      // הקובץ מגיע כ-Blob, לא JSON!
      const blob = await response.blob();

      // קבע את שם הקובץ מה-header של content-disposition
      const contentDisposition = response.headers.get("content-disposition");
      let filename = "";

      if (contentDisposition) {
        // תמיכה ב-UTF-8 encoded filenames (RFC 2231)
        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''(.+?)(?:;|$)/i,
        );
        if (utf8Match && utf8Match[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          // fallback לשם רגיל
          const filenameMatch = contentDisposition.match(
            /filename=["']?([^"';]+)["']?/i,
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }
      }

      // אם לא הצלחנו לקבל שם קובץ מה-header, צור שם לפי שם הדוח + תאריך
      if (!filename) {
        const extension = exportFormat === "pdf" ? "pdf" : "xlsx";
        const timestamp = new Date()
          .toISOString()
          .replace(/[:.]/g, "-")
          .slice(0, -5);
        filename = `${reportData.reportName}_${timestamp}.${extension}`;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: "הורדה בוצעה בהצלחה" };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "שגיאה בהורדה",
      };
    }
  }

  /**
   * שליחת דוח במייל (ללא הורדה מקומית)
   */
  static async sendReportEmail(
    request: ReportEmailRequest,
  ): Promise<ReportEmailResponse> {
    try {
      const payload = {
        ReportId: request.reportId,
        ReportName: request.reportName,
        Tables: request.tables,
        TotalRecords: request.totalRecords,
        Formats: request.formats,
        To: request.to,
        Subject: request.subject,
        Message: request.message,
        Token: request.token,
        CompanyKey: request.companyKey,
      };

      const response = await fetch(`${API_BASE_URL}ReportsExport/sendemail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        return error;
      }

      const result = (await response.json()) as ReportEmailResponse;
      return result;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "שגיאה בשליחת המייל",
      };
    }
  }
}
